import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronRight, CircleHelp, Loader2, MapPinned, Package, PencilRuler, Truck } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpLink } from "@/components/help/HelpLink";
import { DashboardHeader } from "@/components/DashboardHeader";
import { LogisticsSetupTour } from "@/components/ProductTour";
import { ClinicalPageShell, ClinicalSection } from "@/components/clinical";
import { Button } from "@/components/ui/button";
import { ProviderBrandLogo } from "@/components/ProviderBrandLogo";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { cn } from "@/lib/utils";
import { logisticsProviderLogo } from "@/lib/provider-logos";
import {
  isLogisticsProviderId,
  type LogisticsProviderId,
} from "@/lib/logistics-providers";
import type { CarrierHealthSnapshot } from "@/lib/logistics-health";
import {
  OWN_LOGISTICS_HUB_CARDS,
  capabilityProviderCounts,
  isLogisticsServiceType,
  listHubCapabilities,
  primaryCapabilityForProvider,
  providersForCapability,
  type LogisticsServiceType,
} from "@/lib/logistics-hub";
import {
  LazyAndreaniSettingsPanel,
  LazyCabifySettingsPanel,
  LazyUberDirectSettingsPanel,
  LazyPedidosYaSettingsPanel,
  LazyRapiboySettingsPanel,
  LazyOcaSettingsPanel,
  LazyCorreoArgentinoSettingsPanel,
  LazyEntregaSettingsPanel,
  LazyShipnowLogisticsPanel,
  LazyShippingSettingsPanel,
  LazyTreggoSettingsPanel,
  LazyZipnovaSettingsPanel,
  LazyPickitSettingsPanel,
  SettingsPanelSuspense,
} from "@/components/settings/lazy-settings-panels";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

export const Route = createFileRoute("/app/logistica")({
  component: LogisticaPage,
  validateSearch: (s: Record<string, unknown>) => ({
    capability:
      typeof s.capability === "string" && isLogisticsServiceType(s.capability)
        ? s.capability
        : undefined,
    provider:
      typeof s.provider === "string" && isLogisticsProviderId(s.provider) ? s.provider : undefined,
    draw: s.draw === "polygon" ? ("polygon" as const) : undefined,
  }),
  head: () => ({ meta: [{ title: "Logística — Structura Market" }] }),
});

interface ProviderRow {
  id: LogisticsProviderId;
  label: string;
  description: string;
  kind: "native" | "carrier";
  lifecycle: string;
  logoUrl?: string | null;
  entitled: boolean;
  configurable: boolean;
  globallyEnabled?: boolean;
  tenant: {
    enabled: boolean;
    configured: boolean;
    validationStatus?: string | null;
    lastValidatedAt?: string | null;
    environment?: string | null;
    mockMode?: boolean | null;
    health?: CarrierHealthSnapshot | null;
  } | null;
}

interface DeliverySnap {
  metodoEntrega: string;
  radioDeliveryKm: number | null;
  deliveryZonesEnabled: boolean;
  deliveryDistanceTiers?: Array<{ minKm: number; maxKm: number | null; priceCents: number | null }>;
  whatsappNumber: string;
  whatsappMessageTemplate: string;
  storefrontTimezone: string;
}

function lifecycleChip(lc: string) {
  const m: Record<string, { label: string; cls: string }> = {
    stable: { label: "Disponible", cls: "bg-emerald-500/15 text-emerald-400" },
    beta: { label: "Beta", cls: "bg-amber-500/15 text-amber-400" },
    upcoming: { label: "Próximamente", cls: "bg-slate-500/15 text-slate-400" },
    deprecated: { label: "Deprecado", cls: "bg-rose-500/15 text-rose-400" },
  };
  const x = m[lc] ?? m.upcoming;
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${x.cls}`}>{x.label}</span>;
}

function providerCardStatus(p: ProviderRow) {
  if (!p.entitled) return { label: "Plan", active: false };
  if (p.lifecycle === "upcoming" || !p.configurable) return { label: "Próximamente", active: false };
  if (p.tenant?.health?.badge && p.kind === "carrier" && (p.tenant.enabled || p.tenant.configured)) {
    return { label: p.tenant.health.badge, active: p.tenant.health.tone === "ok" };
  }
  if (p.tenant?.enabled && (p.kind === "native" || p.tenant.configured || p.id === "entrega")) {
    return { label: "Activo", active: true };
  }
  if (p.tenant?.enabled) return { label: "Sin credenciales", active: false };
  return { label: "Inactivo", active: false };
}

function healthToneClass(tone: string) {
  switch (tone) {
    case "ok":
      return "border-emerald-500/40 bg-emerald-500/10 text-emerald-300";
    case "error":
      return "border-rose-500/40 bg-rose-500/10 text-rose-300";
    case "warning":
      return "border-amber-500/40 bg-amber-500/10 text-amber-300";
    default:
      return "border-border/60 bg-muted/20 text-muted-foreground";
  }
}

function LogisticaPage() {
  const tid = useWorkspaceTenantId();
  const navigate = useNavigate();
  const { capability: capabilitySearch, provider: providerSearch, draw: drawSearch } = Route.useSearch();

  const hubCapabilities = useMemo(() => listHubCapabilities(), []);

  const resolvedCapability: LogisticsServiceType | undefined = useMemo(() => {
    if (capabilitySearch) return capabilitySearch;
    if (providerSearch) return primaryCapabilityForProvider(providerSearch);
    return undefined;
  }, [capabilitySearch, providerSearch]);

  const selectedProvider = providerSearch;
  const level: 1 | 2 | 3 = selectedProvider ? 3 : resolvedCapability ? 2 : 1;

  useEffect(() => {
    if (selectedProvider === "zones") {
      setConfig((c) => (c.deliveryZonesEnabled ? c : { ...c, deliveryZonesEnabled: true }));
    } else if (selectedProvider === "radius") {
      setConfig((c) => (!c.deliveryZonesEnabled ? c : { ...c, deliveryZonesEnabled: false }));
    }
  }, [selectedProvider]);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [config, setConfig] = useState({
    metodoEntrega: "ambos",
    radioDeliveryKm: "",
    deliveryZonesEnabled: false,
    deliveryDistanceTiers: [] as Array<{ minKm: number; maxKm: number | null; priceCents: number | null }>,
    whatsappNumber: "",
    whatsappMessageTemplate: "",
  });
  const [storefrontTimezone, setStorefrontTimezone] = useState("America/Argentina/Buenos_Aires");

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const res = await apiGetJson<{ providers: ProviderRow[]; delivery: DeliverySnap }>(
        "/api/tenant/logistics-providers",
        tid,
      );
      setProviders(res.providers);
      setConfig({
        metodoEntrega: res.delivery.metodoEntrega || "ambos",
        radioDeliveryKm:
          res.delivery.radioDeliveryKm != null ? String(res.delivery.radioDeliveryKm) : "",
        deliveryZonesEnabled: Boolean(res.delivery.deliveryZonesEnabled),
        deliveryDistanceTiers: Array.isArray(res.delivery.deliveryDistanceTiers)
          ? res.delivery.deliveryDistanceTiers
          : [],
        whatsappNumber: res.delivery.whatsappNumber || "",
        whatsappMessageTemplate: res.delivery.whatsappMessageTemplate || "",
      });
      setStorefrontTimezone(res.delivery.storefrontTimezone || "America/Argentina/Buenos_Aires");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar logística");
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    void load();
  }, [load]);

  const goHub = (next: {
    capability?: LogisticsServiceType;
    provider?: LogisticsProviderId;
    draw?: "polygon";
  }) => {
    void navigate({
      to: "/app/logistica",
      search: {
        capability: next.capability,
        provider: next.provider,
        draw: next.draw,
      },
    });
  };

  const saveEntrega = async (patch: Record<string, unknown>) => {
    if (!tid) return;
    setBusy(true);
    try {
      await apiSendJson("/api/tenant/config", "PATCH", patch, tid);
      toast.success("Logística guardada");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  const selectedMeta = useMemo(
    () => (selectedProvider ? providers.find((p) => p.id === selectedProvider) ?? null : null),
    [providers, selectedProvider],
  );

  const capabilityMeta = useMemo(
    () => hubCapabilities.find((c) => c.id === resolvedCapability) ?? null,
    [hubCapabilities, resolvedCapability],
  );

  const capabilityProviders = useMemo(
    () =>
      resolvedCapability ? providersForCapability(resolvedCapability, providers) : [],
    [resolvedCapability, providers],
  );

  const carrierHealthRows = useMemo(
    () =>
      providers.filter(
        (p) =>
          p.kind === "carrier" &&
          p.configurable &&
          p.entitled &&
          (p.tenant?.enabled || p.tenant?.configured || p.tenant?.health?.tone === "error"),
      ),
    [providers],
  );

  return (
    <div>
      <LogisticsSetupTour />
      <DashboardHeader
        title="Logística"
        subtitle="Empezá por logística propia (incluye dibujar el área en el mapa) o por el tipo de entrega"
      />
      <ClinicalPageShell className="space-y-6">
        <MerchantExperienceBar screenId="logistica" />
        <nav
          className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground"
          aria-label="Ruta del Logistics Hub"
          data-testid="logistics-hub-breadcrumb"
        >
          <button
            type="button"
            className={cn(
              "rounded px-1 py-0.5 hover:text-foreground",
              level === 1 && "font-semibold text-foreground",
            )}
            onClick={() => goHub({})}
          >
            Capacidades
          </button>
          {capabilityMeta ? (
            <>
              <ChevronRight className="h-3 w-3 opacity-50" aria-hidden />
              <button
                type="button"
                className={cn(
                  "rounded px-1 py-0.5 hover:text-foreground",
                  level === 2 && "font-semibold text-foreground",
                )}
                onClick={() => goHub({ capability: capabilityMeta.id })}
              >
                {capabilityMeta.label}
              </button>
            </>
          ) : null}
          {selectedMeta ? (
            <>
              <ChevronRight className="h-3 w-3 opacity-50" aria-hidden />
              <span className="font-semibold text-foreground px-1 py-0.5">{selectedMeta.label}</span>
            </>
          ) : null}
        </nav>

        {level === 1 ? (
          <TooltipProvider delayDuration={200}>
            <div className="space-y-6">
              <ClinicalSection
                title="Logística propia"
                description="Tu flota o tu cobertura local. Acá se configura el retiro, el radio, las zonas y el dibujo del polígono en el mapa."
              >
                <div
                  className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
                  data-testid="logistics-own-grid"
                >
                  {OWN_LOGISTICS_HUB_CARDS.map((card) => {
                    const key = `${card.id}-${card.draw ?? "std"}`;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() =>
                          goHub({
                            capability: card.capabilityHint,
                            provider: card.id,
                            draw: card.draw,
                          })
                        }
                        className="flex flex-col items-start gap-2 rounded-xl border border-border bg-muted/40 p-4 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
                        data-testid={`logistics-own-${key}`}
                      >
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-background/80 text-foreground">
                          {card.draw === "polygon" ? (
                            <PencilRuler className="h-4 w-4" />
                          ) : (
                            <MapPinned className="h-4 w-4" />
                          )}
                        </div>
                        <p className="text-sm font-semibold text-foreground">{card.title}</p>
                        <p className="text-[11px] leading-snug text-muted-foreground">{card.description}</p>
                        {card.draw === "polygon" ? (
                          <span className="mt-auto rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary">
                            Abrir mapa para dibujar
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </ClinicalSection>

              <ClinicalSection
                title="Capacidades de entrega"
                description="Qué tipo de entrega querés ofrecer. Después elegís si lo cubre tu flota o un carrier externo."
              >
                {loading && providers.length === 0 ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
                  </div>
                ) : (
                  <div
                    className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
                    data-testid="logistics-capability-grid"
                  >
                    {hubCapabilities.map((cap) => {
                      const counts = capabilityProviderCounts(cap.id, providers);
                      return (
                        <button
                          key={cap.id}
                          type="button"
                          onClick={() => goHub({ capability: cap.id })}
                          className="flex flex-col items-start gap-2 rounded-xl border border-border bg-muted/40 p-4 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
                          data-testid={`logistics-capability-${cap.id}`}
                        >
                          <div className="flex w-full items-start justify-between gap-2">
                            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-background/80 text-foreground">
                              <Package className="h-4 w-4" />
                            </div>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-background/80 hover:text-foreground"
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`Más info: ${cap.label}`}
                                >
                                  <CircleHelp className="h-4 w-4" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-[240px] text-left leading-snug">
                                {cap.description}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <p className="text-sm font-semibold text-foreground">{cap.label}</p>
                          <p className="text-[11px] leading-snug text-muted-foreground">{cap.description}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {counts.active > 0
                              ? `${counts.active} activo${counts.active === 1 ? "" : "s"}`
                              : "Ninguno activo"}
                            {" · "}
                            {counts.entitled}/{counts.total} disponibles en tu plan
                          </p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </ClinicalSection>
            </div>
          </TooltipProvider>
        ) : null}

        {level === 2 && capabilityMeta ? (
          <ClinicalSection
            title={capabilityMeta.label}
            description="Proveedores que cubren esta capacidad. Configurá el que quieras usar."
          >
            {loading && providers.length === 0 ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
              </div>
            ) : capabilityProviders.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No hay proveedores disponibles para esta capacidad en tu plan.
              </p>
            ) : (
              <div
                className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
                data-testid="logistics-capability-providers"
              >
                {capabilityProviders.map((p) => {
                  const st = providerCardStatus(p);
                  return (
                    <div
                      key={p.id}
                      className={cn(
                        "flex flex-col gap-3 rounded-xl border border-border bg-muted/50 p-4",
                        st.active && "border-primary/40 bg-primary/5",
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <ProviderBrandLogo
                          src={p.logoUrl ?? logisticsProviderLogo(p.id)}
                          alt={p.label}
                          fallback={
                            p.kind === "native" ? (
                              <MapPinned className="h-4 w-4" />
                            ) : (
                              <Truck className="h-4 w-4" />
                            )
                          }
                        />
                        <div className="min-w-0 flex-1 space-y-1">
                          <p className="truncate text-sm font-semibold">{p.label}</p>
                          <p className="line-clamp-2 text-[11px] text-muted-foreground">
                            {p.description}
                          </p>
                          <div className="flex flex-wrap gap-1">{lifecycleChip(p.lifecycle)}</div>
                          <p
                            className={cn(
                              "text-[10px]",
                              st.active ? "text-emerald-400" : "text-muted-foreground",
                            )}
                          >
                            {st.label}
                          </p>
                        </div>
                      </div>
                      {p.entitled && p.configurable ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          className="h-8 w-full text-xs"
                          onClick={() =>
                            goHub({ capability: capabilityMeta.id, provider: p.id })
                          }
                        >
                          Configurar
                        </Button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </ClinicalSection>
        ) : null}

        {level === 3 && selectedProvider ? (
          <ClinicalSection
            title={selectedMeta?.label ?? "Configuración"}
            description={
              selectedMeta?.description ?? "Ajustá credenciales y opciones de este proveedor."
            }
          >
            {!tid ? (
              <p className="text-sm text-muted-foreground">Seleccioná un espacio de trabajo.</p>
            ) : (
              <SettingsPanelSuspense>
                {selectedProvider === "entrega" ||
                selectedProvider === "zones" ||
                selectedProvider === "radius" ? (
                  <LazyEntregaSettingsPanel
                    config={config}
                    setConfig={setConfig}
                    storefrontTimezone={storefrontTimezone}
                    setStorefrontTimezone={setStorefrontTimezone}
                    busy={busy}
                    compact
                    focusSections={
                      selectedProvider === "zones" || selectedProvider === "radius"
                        ? ["cobertura"]
                        : ["metodos"]
                    }
                    zonesInitialMatchType={
                      selectedProvider === "zones" && drawSearch === "polygon"
                        ? "polygon"
                        : undefined
                    }
                    onSaveEntrega={() =>
                      void saveEntrega({
                        metodo_entrega: config.metodoEntrega,
                        radio_delivery_km: config.radioDeliveryKm
                          ? Number(config.radioDeliveryKm)
                          : null,
                        delivery_zones_enabled: config.deliveryZonesEnabled,
                        delivery_distance_tiers: config.deliveryDistanceTiers ?? [],
                        whatsapp_number: config.whatsappNumber || null,
                        whatsapp_message_template: config.whatsappMessageTemplate || null,
                      })
                    }
                    onSaveTimezone={() =>
                      void saveEntrega({ storefront_timezone: storefrontTimezone })
                    }
                  />
                ) : null}
                {selectedProvider === "cabify" ? <LazyCabifySettingsPanel tenantId={tid} /> : null}
                {selectedProvider === "uber_direct" ? (
                  <LazyUberDirectSettingsPanel tenantId={tid} />
                ) : null}
                {selectedProvider === "pedidosya" ? (
                  <LazyPedidosYaSettingsPanel tenantId={tid} />
                ) : null}
                {selectedProvider === "rapiboy" ? <LazyRapiboySettingsPanel tenantId={tid} /> : null}
                {selectedProvider === "zipnova" ? <LazyZipnovaSettingsPanel tenantId={tid} /> : null}
                {selectedProvider === "pickit" ? <LazyPickitSettingsPanel tenantId={tid} /> : null}
                {selectedProvider === "shipnow" ? (
                  <LazyShipnowLogisticsPanel tenantId={tid} />
                ) : null}
                {selectedProvider === "treggo" ? <LazyTreggoSettingsPanel tenantId={tid} /> : null}
                {selectedProvider === "oca" ? <LazyOcaSettingsPanel tenantId={tid} /> : null}
                {selectedProvider === "correo_argentino" ? (
                  <LazyCorreoArgentinoSettingsPanel tenantId={tid} />
                ) : null}
                {selectedProvider === "andreani" ? (
                  <LazyAndreaniSettingsPanel tenantId={tid} />
                ) : null}
                {selectedProvider === "enviopack" ? (
                  <LazyShippingSettingsPanel tenantId={tid} />
                ) : null}
              </SettingsPanelSuspense>
            )}
          </ClinicalSection>
        ) : null}

        {carrierHealthRows.length > 0 ? (
          <ClinicalSection
            title="Estado de carriers"
            description="Secundario: mock vs live y última validación. Usá el hub de capacidades para configurar."
          >
            <div
              className="flex flex-wrap gap-2"
              data-testid="logistics-health-strip"
            >
              {carrierHealthRows.map((p) => {
                const health = p.tenant?.health;
                const tone = health?.tone ?? "muted";
                const capForHealth =
                  resolvedCapability &&
                  providersForCapability(resolvedCapability, [p]).length > 0
                    ? resolvedCapability
                    : primaryCapabilityForProvider(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() =>
                      goHub({
                        capability: capForHealth,
                        provider: p.id,
                      })
                    }
                    className={cn(
                      "inline-flex min-w-[140px] flex-col gap-0.5 rounded-lg border px-3 py-2 text-left transition-colors hover:brightness-110",
                      healthToneClass(tone),
                      selectedProvider === p.id && "ring-2 ring-primary/40",
                    )}
                    data-testid={`logistics-health-${p.id}`}
                  >
                    <span className="text-xs font-semibold">{p.label}</span>
                    <span className="text-[10px] opacity-90">
                      {health?.badge ?? "—"}
                      {health?.mode && health.mode !== "unknown"
                        ? ` · ${health.mode === "mock" ? "mock" : "live"}`
                        : ""}
                    </span>
                    <span className="text-[10px] opacity-70">{health?.label ?? "Sin datos"}</span>
                    {health?.goLive ? (
                      <span
                        className={cn(
                          "text-[9px] font-medium",
                          health.goLive.readyForProd ? "opacity-90" : "opacity-60",
                        )}
                        data-testid={`logistics-golive-${p.id}`}
                      >
                        {health.goLive.criteriaLabel}
                      </span>
                    ) : null}
                    {p.tenant?.lastValidatedAt ? (
                      <span className="text-[9px] opacity-60 tabular-nums">
                        Validado {new Date(p.tenant.lastValidatedAt).toLocaleDateString("es-AR")}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            {(() => {
              const selectedCarrier = carrierHealthRows.find((p) => p.id === selectedProvider);
              const goLive = selectedCarrier?.tenant?.health?.goLive;
              if (!selectedCarrier || !goLive) return null;
              return (
                <div
                  className="mt-3 rounded-lg border border-border/50 bg-card/30 p-3"
                  data-testid="carrier-golive-checklist"
                >
                  <p className="text-xs font-semibold text-foreground">
                    Checklist go-live · {selectedCarrier.label}
                  </p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    Criterios: credenciales · webhook · cotización real · prueba OK
                    {selectedCarrier.id === "pedidosya" || selectedCarrier.id === "rapiboy"
                      ? goLive.readyForProd
                        ? " — Checklist técnico OK (sigue en mock/demo hasta homologación vendor)"
                        : ""
                      : goLive.readyForProd
                        ? " — Listo para prod"
                        : ""}
                    . Homologación PedidosYa/Rapiboy sigue{" "}
                    <span className="font-medium text-foreground">bloqueada sin creds vendor</span>{" "}
                    (demo / ACCEPTED BLOCKED).
                  </p>
                  <div className="mt-1" data-testid="carrier-golive-help">
                    <HelpLink articleId="delivery" label="Ayuda entrega / carriers" variant="subtle" />
                  </div>
                  <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                    {goLive.checks.map((c) => (
                      <li
                        key={c.id}
                        className={cn(
                          "flex items-start gap-2 rounded-md px-2 py-1.5 text-xs",
                          c.done ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-200",
                        )}
                      >
                        <span className="mt-0.5 font-mono text-[10px]">{c.done ? "✓" : "○"}</span>
                        <span>
                          <span className="font-medium">{c.label}</span>
                          {!c.done && c.hint ? (
                            <span className="mt-0.5 block text-[10px] opacity-80">{c.hint}</span>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </ClinicalSection>
        ) : null}
      </ClinicalPageShell>
    </div>
  );
}
